import {
  useIndicatorStore,
  useLayerStore,
  useUtilStore
} from "@/data/Stores";
import { LocationLevelEnum, StoreEnum } from "@/enums";
import BoundaryApi from "@/services/Boundary";
import { IBoundary, IMaybe, IPhc, IPhcQuery, ISelect } from "@/types";
import { AppNotification } from "@/utils";
import AppLogger from "@/utils/Logger";
import { isBoundary } from "@/utils/Misc";
import { create, StateCreator } from "zustand";
import { devtools } from "zustand/middleware";
import useRevitalizationStore from "./Revitalization";

export interface HFLState {
  selectedHF: IMaybe<number | string>;
  selectedHFDetail: IMaybe<IPhc>;
  HFList: IMaybe<ISelect[]>;
  phcQuery: IMaybe<string>;
  phcData: IMaybe<IPhcQuery>;
}

export interface HFLActions {
  setSelectedHF: (state: IMaybe<number | string>) => void;
  setSelectedHFDetail: (state: IMaybe<IPhc>) => void;
  setHFList: (HFList: IMaybe<ISelect[]>) => void;
  onHFChange: (id: IMaybe<string>) => void;
  getBoundaryByPHC: (query?: IMaybe<string>) => void;
  setPhcQuery: (query: IMaybe<string>) => void;
  setPhcData: (phcData: IMaybe<IPhcQuery>) => void;
}

export const storeCreator: StateCreator<HFLState & HFLActions> = (set) => ({
  selectedHF: null,
  selectedHFDetail: null,
  HFList: null,
  phcQuery: null,
  phcData: null,
  setSelectedHF: (selectedHF) => set({ selectedHF }),
  setHFList: (HFList) => set({ HFList }),
  setSelectedHFDetail: (selectedHFDetail) => set({ selectedHFDetail }),
  onHFChange: async (id) => {
    const { setIsPHCView, toggleRightNav } = useUtilStore.getState();
    const { setSelectedHF, getBoundaryByPHC } = useHFStore.getState();
    const { getRevitalizationById } = useRevitalizationStore.getState();

    if (!id) {
      setSelectedHF(null);
      setIsPHCView(false);
      toggleRightNav(false);
      return;
    }

    setSelectedHF(id);
    getRevitalizationById(`?phc_id=${id}&details=true&status=false`);
    getBoundaryByPHC();
  },
  getBoundaryByPHC: async (query = null) => {
    const { selectedHF, setSelectedHFDetail } = useHFStore.getState();
    const { setIsLoading, setIsPHCView } = useUtilStore.getState();
    const { setDashboardData } = useIndicatorStore.getState();
    const { loadBoundary } = useLayerStore.getState();
    // const { resetSelections } = useZoneStore.getState();

    // resetSelections();
    // set the dashboard data
    if (!selectedHF && !query) return;

    try {
      setIsLoading(true);
      setSelectedHFDetail(null);

      const $PHCData = await BoundaryApi.getPHC({
        id: selectedHF as string,
        query,
      });

      // Map Point Click
      if (!query && isBoundary($PHCData)) {
        const _phcData = $PHCData as IBoundary;
        const __phcDataWithId = {
          ...(_phcData?.data?.features[0]?.properties as unknown as IPhc),
          id: selectedHF as number,
        };
        setSelectedHFDetail(__phcDataWithId);
        setIsPHCView(true);
      }

      // Search or Find PHC close to me
      if (query && isBoundary($PHCData)) {
        const noRecordFound =
          !$PHCData?.data?.geo_data?.length ||
          $PHCData?.data?.geo_data?.length === 0 ||
          !$PHCData?.data?.features?.length ||
          $PHCData?.data?.features?.length === 0;
        if (noRecordFound)
          return AppNotification.info({
            msg: "Please try again",
            desc: "The search criteria did not return any results",
            placement: "bottom",
            showProgress: true,
          });
        setDashboardData({
          staff_accommodation_availability:
            $PHCData?.data?.staff_accommodation_availability || 0,
          total_bhcpf_impact_funds:
            $PHCData?.data?.total_bhcpf_impact_funds || 0,
          total_count: $PHCData?.data?.total_count || 0,
          total_facility_functionality_1:
            $PHCData?.data?.total_facility_functionality_1 || 0,
          total_facility_functionality_2:
            $PHCData?.data?.total_facility_functionality_2 || 0,
          total_facility_functionality_partial:
            $PHCData?.data?.total_facility_functionality_partial || 0,
          total_number_of_chos: $PHCData?.data?.total_number_of_chos || 0,
          total_number_of_jchews: $PHCData?.data?.total_number_of_jchews || 0,
          total_number_of_lab_techs:
            $PHCData?.data?.total_number_of_lab_techs || 0,
          total_number_of_medical_officers:
            $PHCData?.data?.total_number_of_medical_officers || 0,
          total_number_of_nurses: $PHCData?.data?.total_number_of_nurses || 0,
          total_number_of_pharmacy_techs:
            $PHCData?.data?.total_number_of_pharmacy_techs || 0,
          total_number_of_sba: $PHCData?.data?.total_number_of_sba || 0,
          total_number_of_schews: $PHCData?.data?.total_number_of_schews || 0,
          total_sba_gaps: $PHCData?.data?.total_sba_gaps || 0,
          functional_backup_power: $PHCData?.data?.functional_backup_power || 0,
          phc_count: [],
        });
        loadBoundary([$PHCData], LocationLevelEnum.WARD, false);
      }
    } catch (error) {
      AppLogger.error(
        "Error on getBoundaryByPHC =>",
        error as Error,
        selectedHF
      );
    } finally {
      setIsLoading(false);
    }
  },
  setPhcQuery: (query) => set({ phcQuery: query }),
  setPhcData: (phcData) => set({ phcData }),
});

const useHFStore = create<HFLState & HFLActions>(
  devtools(storeCreator, { name: StoreEnum.HFL_STORE }) as StateCreator<
    HFLState & HFLActions
  >
);

export default useHFStore;
